import type { MouseEvent, ReactNode } from 'react'

type IconButtonProps = {
  /** Accessible name; also the tooltip. */
  label: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  children: ReactNode
  variant?: 'plain' | 'accent' | 'danger' | 'destructive'
  disabled?: boolean
  outlined?: boolean
  className?: string
  /** Diameter of the painted circle. The hit area is never smaller than 44px, whatever this says. */
  size?: number
}

const VARIANTS: Record<NonNullable<IconButtonProps['variant']>, string> = {
  plain: 'text-muted group-hover:text-ink group-hover:bg-surface2',
  accent: 'bg-accent text-on-accent group-hover:bg-accent-strong',
  danger: 'bg-danger text-on-danger group-hover:brightness-110',
  // A destructive action inside a list row: still red, but it does not shout from every row the way
  // a filled button does.
  destructive: 'text-danger group-hover:bg-danger/15',
}

/**
 * A 44px touch target that can still paint a smaller circle — the same split apps/web uses: WCAG
 * 2.5.5 wants 44px, dense admin rows want less.
 */
export function IconButton({
  label,
  onClick,
  children,
  variant = 'plain',
  disabled = false,
  outlined = false,
  className = '',
  size = 44,
}: IconButtonProps) {
  const hit = Math.max(44, size)
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`press group inline-flex shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      style={{ width: hit, height: hit }}
    >
      <span
        className={`flex items-center justify-center rounded-full ${outlined ? 'ring-1 ring-line-strong' : ''} ${VARIANTS[variant]}`}
        style={{ width: size, height: size }}
      >
        {children}
      </span>
    </button>
  )
}
